// External dependencies.
import * as PropTypes from "prop-types";
import React, { memo } from "react";
import { createGlobalStyle } from "styled-components";
import { defineMessages, IntlProvider } from "react-intl";
import { connect, Provider } from "react-redux";
import { ConnectedRouter } from "connected-react-router";

import { matchPath, Redirect, Route, Switch } from "react-router-dom";
import DocumentTitle from "react-document-title";
import queryString from "query-string";
import omit from "lodash/omit";
import isEqual from "lodash/isEqual";
import { Root } from "@yoast/ui-library";

// Internal dependencies.
import "./App.css";
import menuItems, { isAvailable } from "./config/Menu";
import { inLoginLayout, inMainLayout, inSingleLayout, inWhiteLayout, inLightGreyLayout, LoginLayout } from "./components/Layout";
import { getDocumentTitle } from "./components/Layout/helpers";
import * as layoutStyles from "./components/Layout/styles.module.scss";
import Footer from "./components/Layout/Footer";
import PageNotFound from "./components/PageNotFound";
import RouteCrossfade from "./components/RouteCrossfade";
import AccountDisabled from "./components/AccountDisabled";
import SitePageContainer from "./containers/SitePage";
import SubscriptionPageContainer from "./containers/SubscriptionPage";
import ProfileDetails from "./containers/ProfileDetails";
import AlmostThere from "./components/login/AlmostThere";
import LoginPage from "./components/login/LoginSignupPage";
import ResetPasswordEmailContainer from "./containers/ResetPasswordEmail";
import ResetPasswordContainer from "./containers/ResetPassword";
import SendResetEmailSuccessPage from "./components/login/SendResetEmailSuccessPage";
import ResetPasswordSuccessPage from "./components/login/ResetPasswordSuccessPage";

import {
	getRedirectUrl,
	hasPeriLoginCookie,
	removePeriLoginCookie,
	sanitizeRedirectUrl,
	setPeriLoginCookie,
} from "shared-frontend/functions/auth";
import ActivateContainer from "./containers/ActivateContainer";
import ConsentScreen from "./components/oauth/ConsentScreen";
import Loader from "./components/Loader";
import InstallationFlow from "./components/install/InstallationFlow";
import { UserContextProvider } from "./context/UserContext";
import PluginInstallerWrapper from "./components/PluginInstaller/PluginInstallerWrapper";
import { SubscriptionDetailProvider } from "./context/SubscriptionDetailContext";
import { AcceptEmailInvite } from "./components/AcceptEmailInvite";
import { getUserId } from "./redux/selectors/ui/user";
import CheckoutLoading from "./components/CheckoutLoading";
import WinbackRedirect from "./components/WinbackRedirect";

const titles = defineMessages( {
	login: {
		id: "menu.title.login",
		defaultMessage: "Login",
	},
	signup: {
		id: "menu.title.signup",
		defaultMessage: "Signup",
	},
	accountActivate: {
		id: "menu.title.accountActivate",
		defaultMessage: "Activate account",
	},
	accountEnterDetails: {
		id: "menu.title.accountEnterDetails",
		defaultMessage: "Enter account details",
	},
	passwordForgot: {
		id: "menu.title.passwordForgot",
		defaultMessage: "Reset your password",
	},
	passwordCheckEmail: {
		id: "menu.title.passwordCheckEmail",
		defaultMessage: "Check your email",
	},
	passwordReset: {
		id: "menu.title.passwordReset",
		defaultMessage: "Enter a new password",
	},
	passwordSuccess: {
		id: "menu.title.passwordSuccess",
		defaultMessage: "Password changed successfully!",
	},
	manageSite: {
		id: "menu.title.manageSite",
		defaultMessage: "Manage site",
	},
	manageSubscription: {
		id: "menu.title.manageSubscription",
		defaultMessage: "Manage subscription",
	},
	notFound: {
		id: "menu.title.notFound",
		defaultMessage: "Page not found",
	},
	connect: {
		id: "menu.title.connect",
		defaultMessage: "Connect",
	},
	install: {
		id: "menu.title.install",
		defaultMessage: "Install your products",
	},
	downloading: {
		id: "menu.title.install",
		defaultMessage: "Downloading your products",
	},
	oauthConsent: {
		id: "menu.title.oauthConsent",
		defaultMessage: "Authorize application",
	},
	acceptInvite: {
		id: "menu.title.acceptInvite",
		defaultMessage: "Almost there!",
	},
} );

// Login and signup share one route so LoginSignupPage, which switches between the two forms, stays mounted.
const loginSignupRoute = {
	path: [ "/login", "/signup" ],
	component: LoginPage,
	title: ( location ) => ( location.pathname.startsWith( "/signup" ) ? titles.signup : titles.login ),
};

const forgotPasswordRoutes = [
	{ path: "/forgot-password", component: ResetPasswordEmailContainer, title: titles.passwordForgot },
	{ path: "/forgot-password/check-your-email", component: SendResetEmailSuccessPage, title: titles.passwordCheckEmail },
	{ path: "/forgot-password/reset-password", component: ResetPasswordContainer, title: titles.passwordReset },
	{ path: "/forgot-password/success", component: ResetPasswordSuccessPage, title: titles.passwordSuccess },
];

const loggedOutLoginRoutes = [
	loginSignupRoute,
	{ path: "/activate", component: ActivateContainer, title: titles.accountActivate },
	{ path: "/almost-there", component: AlmostThere, title: titles.accountActivate },
	...forgotPasswordRoutes,
];

const activatingLoginRoutes = [
	{ path: "/activate", component: ActivateContainer, title: titles.accountActivate },
	{ path: "/enter-details", component: ProfileDetails, title: titles.accountEnterDetails },
	...forgotPasswordRoutes,
];

const AcceptInvitePage = inLoginLayout(
	( routerProps ) => <AcceptEmailInvite { ...routerProps.match.params } />,
	titles.acceptInvite.defaultMessage,
);

const OAUTH_CONSENT_PATH = "/oauth/consent";

const OAuthConsentPage = inLoginLayout( ConsentScreen, titles.oauthConsent.defaultMessage );

/**
 * Renders routes that share one LoginLayout, so the layout stays mounted while navigating between them.
 *
 * The page content crossfades when the route changes; paths of one route, like login and signup, share their content.
 *
 * @param {string} key    The route key.
 * @param {Array}  routes The routes as `{ path, component, title }`. `title` is a message descriptor,
 *                        or a function that gets the location and returns one.
 *
 * @returns {ReactElement} One route that matches all the given paths.
 */
const renderLoginLayoutRoutes = ( key, routes ) => {
	const getRouteKey = ( location ) => {
		const route = routes.find( ( { path } ) => matchPath( location.pathname, { path, exact: true } ) );

		return route ? String( route.path ) : location.pathname;
	};

	return (
		<Route exact={ true } key={ key } path={ routes.flatMap( route => route.path ) }>
			<LoginLayout>
				<RouteCrossfade getKey={ getRouteKey }>
					{ location => (
						<Switch location={ location }>
							{ routes.map( ( { path, component: RouteComponent, title } ) => (
								<Route
									exact={ true }
									key={ String( path ) }
									path={ path }
									render={ ( routeProps ) => {
										const message = typeof title === "function" ? title( routeProps.location ) : title;

										return (
											<DocumentTitle title={ getDocumentTitle( message.defaultMessage ) }>
												<RouteComponent { ...routeProps } />
											</DocumentTitle>
										);
									} }
								/>
							) ) }
						</Switch>
					) }
				</RouteCrossfade>
			</LoginLayout>
		</Route>
	);
};

/*
 * Helper method to write global CSS.
 * Only use it for the rare @font-face definition or body styling.
 */

const GlobalStyle = createGlobalStyle`
	body {
		font-family: "Open Sans", sans-serif !important;
		background: var(--bg-color-offwhite);
	}
`;

/**
 * This compares the previousProps with the nextProps.
 * If they aren't the same we should reRender the component.
 *
 * @param {Object} prevProps The previous props.
 * @param {Object} nextProps The next props.
 *
 * @returns {boolean} Are the compaired props equal.
 */
const arePropsEqual = ( prevProps, nextProps ) => {
	const simpleProps     = omit( prevProps, [ "router", "history" ] );
	const simpleNextProps = omit( nextProps, [ "router", "history" ] );

	if ( ! isEqual( simpleNextProps, simpleProps ) ) {
		return false;
	}

	const samePath               = prevProps.router.location && prevProps.router.location.pathname === nextProps.router.location.pathname;
	const clickedContentJumplink = nextProps.router.location && nextProps.router.location.hash === "#content";

	if ( samePath || clickedContentJumplink ) {
		return true;
	}

	return false;
};

/**
 * The Routes component.
 *
 * @param {Object} props The props.
 *
 * @returns {component} The Routes component.
 */
// eslint-disable-next-line react/display-name
const Routes = memo( props => {
	/**
	 * Gets the URL to send a logged-in user to, and forgets the intended destination once used.
	 *
	 * Returns null on the OAuth consent page, which sends the user on once the OAuth interaction resolves.
	 *
	 * @param {string} pathname The current path.
	 *
	 * @returns {string|null} The URL to redirect to, or null to stay on the current page.
	 */
	const getPostLoginRedirect = ( pathname ) => {
		if ( matchPath( pathname, { path: OAUTH_CONSENT_PATH, exact: true } ) ) {
			removePeriLoginCookie();
			return null;
		}

		if ( hasPeriLoginCookie() ) {
			const redirectTo = getRedirectUrl();
			removePeriLoginCookie();
			return redirectTo;
		}

		const queryParams = queryString.parse( window.location.search );
		return queryParams.redirect_to ? sanitizeRedirectUrl( queryParams.redirect_to ) : null;
	};

	/**
	 * Redirects the user to the login page while saving the location the user was actually trying to reach.
	 * Saving this location allows us to redirect the user back to it as soon as the login succeeds.
	 *
	 * @param {import('react-router-dom').RouteProps} routeProps The props of the Route
	 *
	 * @returns {ReactElement} The redirect component.
	 */
	const redirectToLogin = routeProps => {
		removePeriLoginCookie();
		setPeriLoginCookie();

		return ( <Redirect
			to={ {
				pathname: "/login",
				hash: routeProps.location.hash,
				search: routeProps.location.search,
			} }
		/> );
	};

	/**
	 * Pushes the user ID to the data layer for Google Tag Manager.
	 * @param {string} userId The user ID.
	 *
	 * @returns {void}
	 */
	const pushToDataLayer = ( userId ) => {
		window.dataLayer = window.dataLayer || [];

		// if there is already a user ID in the dataLayer, don't push again
		if ( window.dataLayer.some( ( event ) => event.event === "gtm_myyoast_user" ) ) {
			return;
		}
		window.dataLayer.push( {
			event: "gtm_myyoast_user",
			// eslint-disable-next-line camelcase
			user_id: userId ? userId : "unknown",
		} );
	};

	/**
	 * Renders the menu routes.
	 *
	 * @returns {Routes} The menu routes.
	 */
	const renderMenuRoutes = () => {
		// An unavailable item has no route either, so its URL falls through to PageNotFound.
		return menuItems.filter( isAvailable ).map( ( route, routeKey ) => {
			if ( route.inMainLayout === false ) {
				const { component: RouteComponent, ...routeRest } = route;
				return <Route
					{ ...routeRest }
					key={ routeKey }
					path={ route.path }
					render={ ( routeProps ) => (
						<div className={ layoutStyles.bareRouteLayout }>
							<div className={ layoutStyles.bareRouteContent }>
								<RouteComponent { ...routeProps } />
							</div>
							<Footer variant="light" />
						</div>
					) }
				/>;
			}

			return <Route
				{ ...route }
				key={ routeKey }
				path={ route.path }
				component={ inMainLayout( route.component, route.title.defaultMessage, menuItems, route.noBackground ) }
			/>;
		} );
	};

	const routesThatAreAlwaysAvailable = [
		<Route
			exact={ true }
			path="/automatic-install"
			key="automatic-install"
			component={ inLightGreyLayout( PluginInstallerWrapper, titles.downloading.defaultMessage ) }
		/>,
		<Route
			path="/accept-invitation/:subscriptionId/:inviteId/:inviteToken"
			key="invite"
			component={ AcceptInvitePage }
		/>,
		<Route
			exact={ true }
			path="/checkout-loading"
			key="checkout-loading"
			component={ CheckoutLoading }
		/>,
		<Route
			exact={ true }
			path={ OAUTH_CONSENT_PATH }
			key="oauth-consent"
			component={ OAuthConsentPage }
		/>,
	];

	if ( props.loggedIn === false ) {
		return (
			<ConnectedRouter history={ props.history }>
				<Switch>
					{ renderLoginLayoutRoutes( "login-layout", loggedOutLoginRoutes ) }
					{ routesThatAreAlwaysAvailable }
					<Route
						path="*"
						render={ redirectToLogin }
					/>
				</Switch>
			</ConnectedRouter>
		);
	}

	if ( props.userEnabled === false ) {
		return (
			<ConnectedRouter history={ props.history }>
				{ routesThatAreAlwaysAvailable }
				<Route path="*" component={ inSingleLayout( AccountDisabled ) } />
			</ConnectedRouter>
		);
	}

	if ( props.userEnabled === true ) {
		const redirectTo = getPostLoginRedirect( props.router.location?.pathname ?? "" );
		if ( redirectTo ) {
			window.location.replace( redirectTo );
			return null;
		}

		// Push the user ID to the data layer for Google Tag Manager if we're dealing with a logged in user.
		pushToDataLayer( props.userId );

		return (
			<ConnectedRouter history={ props.history }>
				<UserContextProvider accessToken={ props.accessToken }>
					<Switch>
						{ renderLoginLayoutRoutes( "login-layout", activatingLoginRoutes ) }
						<Route
							exact={ true }
							path="/login"
							render={ () => <Redirect to={ "/" } /> }
						/>
						{ /* Redirect URLs that we used in the old pre-ui-library design in case they are linked or bookmarked */ }
						<Route
							exact={ true }
							path="/account"
							render={ () => <Redirect to={ "/subscriptions" } /> }
						/>
						<Route
							exact={ true }
							path="/account/orders"
							render={ () => <Redirect to={ "/orders" } /> }
						/>
						<Route
							exact={ true }
							path="/account/profile"
							render={ () => <Redirect to={ "/profile" } /> }
						/>
						<Route
							exact={ true }
							path="/account/subscriptions"
							render={ () => <Redirect to={ "/subscriptions" } /> }
						/>
						<Route
							exact={ true }
							path="/account/subscriptions/:id"
							render={ ( pageProps ) => <Redirect
								to={ "/subscriptions/" + pageProps.match.params.id }
							/> }
						/>
						<Route
							path="/sites/:id"
							component={ inMainLayout( SitePageContainer, titles.manageSite.defaultMessage, menuItems ) }
						/>
						<Route
							path="/subscriptions/:id"
							component={ inMainLayout(
								( routeProps ) => <SubscriptionDetailProvider
									subscriptionId={ routeProps.match.params.id }
								>
									<SubscriptionPageContainer />
								</SubscriptionDetailProvider>,
								titles.manageSubscription.defaultMessage,
								menuItems,
							) }
						/>
						<Route
							path="/install"
							component={ inWhiteLayout( InstallationFlow, titles.install.defaultMessage ) }
						/>
						<Route
							path="/special-offer/:productReference"
							component={ WinbackRedirect }
						/>
						{ routesThatAreAlwaysAvailable }
						{ renderMenuRoutes() }
						<Route
							path="*"
							component={ inMainLayout( PageNotFound, titles.notFound.defaultMessage, menuItems ) }
						/>;
					</Switch>
				</UserContextProvider>
			</ConnectedRouter>
		);
	}
	// We don't want to render anything until user is fetched, user.enabled is null by default.
	return <Loader />;
}, arePropsEqual );


Routes.propTypes = {
	userEnabled: PropTypes.bool,
	userId: PropTypes.string,
	userData: PropTypes.object.isRequired,
	accessToken: PropTypes.string,
	loggedIn: PropTypes.bool.isRequired,
	history: PropTypes.object,
	completedLogin: PropTypes.bool,
	router: PropTypes.object,
};

Routes.defaultProps = {
	userEnabled: false,
	userId: "",
	accessToken: "",
	completedLogin: false,
	history: {},
	router: {},
};


const mapStateToProps = state => {
	return {
		userEnabled: state.user.enabled,
		userId: getUserId( state ),
		userData: state.user.data.profile,
		accessToken: state.user.accessToken,
		loggedIn: state.user.loggedIn,
		completedLogin: state.ui.login.completedLogin,
		router: state.router,
	};
};


const RoutesContainer = connect(
	mapStateToProps,
)( Routes );

/**
 * The App Component.
 *
 * @param {Object} props The props.
 *
 * @returns {Component} The
 */
const App = props => {
	return (
		<Root>
			<GlobalStyle />
			<IntlProvider locale="en">
				<Provider store={ props.store }>
					<RoutesContainer history={ props.history } />
				</Provider>
			</IntlProvider>
		</Root>
	);
};


App.propTypes = {
	store: PropTypes.object,
	history: PropTypes.object,
};

App.defaultProps = {
	store: {},
	history: {},
};
export default App;
